import type { ReactElement } from 'react';
import type { JourneyArt } from './setup-journey-model';
// standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md

/**
 * The setup screen's pictures (owner-approved October 3, 2026): one small animated line
 * drawing per step, and one per tip. Authored SVG in the illustration recipe's style
 * (illustration-style.md): outlines in currentColor, faces on the raised surface, one
 * small orange mark. Classes are prefixed sja- so artwork nouns never meet a utility.
 * Each drawing is a picture of the work, never a result: counts and names stay in the
 * text beside it. Reduced motion shows the finished drawing (setup-journey.css).
 */

const Doc = ({
  x,
  y,
  w = 20,
  h = 26,
  delay,
  className = '',
}: {
  x: number;
  y: number;
  w?: number;
  h?: number;
  delay?: number;
  className?: string;
}) => (
  <g
    className={className}
    style={delay === undefined ? undefined : { animationDelay: `${delay}s` }}
  >
    <rect className="sja-face" x={x} y={y} width={w} height={h} rx={2.5} />
    <rect className="sja-ink" x={x + 4} y={y + 5} width={w * 0.5} height={3} rx={1} />
  </g>
);

function Reading() {
  const lines = [
    [42, 72],
    [54, 60],
    [66, 68],
    [78, 52],
    [90, 72],
    [102, 64],
    [114, 40],
    [126, 56],
  ];
  return (
    <>
      <rect className="sja-face" x={62} y={10} width={96} height={140} rx={6} />
      <rect className="sja-ink" x={74} y={24} width={40} height={7} rx={2} />
      {lines.map(([y, w]) => (
        <rect key={y} className="sja-line" x={74} y={y} width={w} height={5} rx={2} />
      ))}
      {lines.map(([y, w], index) => (
        <rect
          key={`h${y}`}
          className="sja-glow"
          style={{ animationDelay: `${(0.57 + index * 0.215).toFixed(2)}s` }}
          x={71}
          y={y - 2}
          width={w + 6}
          height={9}
          rx={3}
        />
      ))}
      <g className="sja-beam">
        <rect x={52} y={8} width={116} height={3} rx={1.5} />
        <rect className="sja-beam-soft" x={52} y={-6} width={116} height={14} rx={3} />
      </g>
    </>
  );
}

function Separating() {
  const stack = (shift: string, ink: number) => (
    <g className={shift}>
      <rect className="sja-back" x={97} y={44} width={34} height={46} rx={3} />
      <rect className="sja-face" x={93} y={50} width={34} height={46} rx={3} />
      <rect className="sja-ink" x={98} y={56} width={16} height={4} rx={1.5} />
      <rect className="sja-line" x={98} y={66} width={24} height={3} rx={1} />
      <rect className="sja-line" x={98} y={73} width={20} height={3} rx={1} />
      <rect className="sja-line" x={98} y={80} width={22} height={3} rx={1} />
      <rect
        className="sja-docbar"
        data-type-shade={ink}
        x={93}
        y={104}
        width={34}
        height={3}
        rx={1.5}
      />
    </g>
  );
  return (
    <>
      {stack('sja-split-left', 0)}
      {stack('sja-split-right', 2)}
      {stack('', 1)}
      <line className="sja-cut" x1={80} y1={34} x2={80} y2={112} />
      <line className="sja-cut" x1={140} y1={34} x2={140} y2={112} />
    </>
  );
}

function Types() {
  const bins = [18, 85, 152];
  return (
    <>
      {bins.map((x, index) => (
        <g key={x} data-type-shade={index === 0 ? 0 : index === 1 ? 1 : 2}>
          <rect className="sja-bin" x={x} y={108} width={50} height={34} rx={7} />
          <rect className="sja-bin-ink" x={x + 10} y={128} width={30} height={3} rx={1.5} />
          <rect className="sja-bin-ink" x={x + 10} y={134} width={22} height={3} rx={1.5} />
        </g>
      ))}
      {(
        [
          'sja-drop-left',
          'sja-drop-mid',
          'sja-drop-right',
          'sja-drop-left',
          'sja-drop-mid',
        ] as const
      ).map((drop, index) => (
        <Doc key={index} className={drop} delay={index * 0.7} x={100} y={8} />
      ))}
    </>
  );
}

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
function Fields() {
  const rows = [30, 56, 82, 108];
  const boxes = [
    [20, 20, 46, 14],
    [20, 41, 70, 14],
    [20, 87, 74, 30],
    [52, 121, 42, 15],
  ];
  const wires = [
    'M66 27 C 96 27, 104 30, 124 30',
    'M90 48 C 106 48, 110 56, 124 56',
    'M94 102 C 110 102, 110 82, 124 82',
    'M94 128 C 110 128, 112 108, 124 108',
  ];
  return (
    <>
      <rect className="sja-face" x={14} y={14} width={86} height={130} rx={6} />
      <rect className="sja-ink" x={24} y={24} width={38} height={6} rx={2} />
      {[46, 68, 92, 100, 108].map((y) => (
        <rect key={y} className="sja-line" x={24} y={y} width={62} height={4} rx={2} />
      ))}
      <rect className="sja-line" x={56} y={126} width={34} height={5} rx={2} />
      {boxes.map(([x, y, w, h], index) => (
        <rect
          key={index}
          className="sja-pick sja-step"
          style={{ animationDelay: `${index * 0.6}s` }}
          x={x}
          y={y}
          width={w}
          height={h}
          rx={3}
        />
      ))}
      {wires.map((d, index) => (
        <path
          key={index}
          className="sja-wire"
          style={{ animationDelay: `${index * 0.6}s` }}
          d={d}
        />
      ))}
      {rows.map((y, index) => (
        <g key={y} className="sja-step" style={{ animationDelay: `${0.25 + index * 0.6}s` }}>
          <rect className="sja-box" x={124} y={y - 7} width={14} height={14} rx={3} />
          <path className="sja-tick" d={`M127 ${y}l3 3 5-6`} />
          <rect
            className="sja-line"
            x={146}
            y={y - 3}
            width={[50, 58, 44, 38][index]}
            height={6}
            rx={2}
          />
        </g>
      ))}
    </>
  );
}

function Values() {
  const rows: [number, number, number][] = [
    [40, 12, 46],
    [62, 12, 68],
    [84, 30, 99],
    [122, 14, 129],
  ];
  return (
    <>
      <rect className="sja-face" x={34} y={12} width={104} height={136} rx={6} />
      <rect className="sja-ink" x={46} y={24} width={44} height={6} rx={2} />
      {rows.map(([y, h], index) => (
        <rect
          key={y}
          className="sja-found"
          style={{ animationDelay: `${0.2 + index * 0.5}s` }}
          x={44}
          y={y}
          width={82}
          height={h}
          rx={3}
        />
      ))}
      {[44, 66, 88, 96, 104].map((y) => (
        <rect key={y} className="sja-line" x={48} y={y} width={70} height={4} rx={2} />
      ))}
      <rect className="sja-line" x={80} y={127} width={40} height={5} rx={2} />
      {rows.map(([, , cy], index) => (
        <g key={cy} className="sja-ok" style={{ animationDelay: `${0.35 + index * 0.5}s` }}>
          <circle cx={156} cy={cy} r={9} />
          <path d={`M151.5 ${cy}l3 3 6-6`} />
        </g>
      ))}
    </>
  );
}

function Files() {
  return (
    <>
      <rect className="sja-lamp-beam" x={96} y={66} width={28} height={48} />
      <path className="sja-arch" d="M90 116 V58 Q90 52 96 52 H124 Q130 52 130 58 V116" />
      <rect className="sja-lamp" x={98} y={57} width={24} height={3} rx={1.5} />
      <rect className="sja-belt" x={6} y={116} width={208} height={8} rx={4} />
      {[22, 62, 102, 142, 182].map((x) => (
        <circle key={x} className="sja-roller" cx={x} cy={120} r={3} />
      ))}
      {[0, 1.5, 3].map((delay) => (
        <g key={delay} className="sja-ride" style={{ animationDelay: `${delay}s` }}>
          <rect className="sja-face" x={-30} y={84} width={24} height={32} rx={2.5} />
          <rect className="sja-ink" x={-26} y={89} width={12} height={3} rx={1} />
          <rect className="sja-line" x={-26} y={96} width={16} height={2} rx={1} />
          <g className="sja-passed" style={{ animationDelay: `${delay}s` }}>
            <circle cx={-8} cy={86} r={7} />
            <path d="M-11 86l2 2 4-4" />
          </g>
        </g>
      ))}
    </>
  );
}

function Done() {
  return (
    <>
      <circle className="sja-done-fill" cx={110} cy={80} r={40} />
      <circle className="sja-done-pulse" cx={110} cy={80} r={40} />
      <circle className="sja-done-ring" cx={110} cy={80} r={40} transform="rotate(-90 110 80)" />
      <path className="sja-done-tick" d="M92 81l13 13 25-27" />
    </>
  );
}

const ART: Record<JourneyArt, () => ReactElement> = {
  reading: Reading,
  separating: Separating,
  types: Types,
  fields: Fields,
  values: Values,
  files: Files,
  done: Done,
};

/** The picture beside the step being done. Keyed by art, so a new step starts its own loop. */
export function SetupStepArt({ art }: { art: JourneyArt }) {
  const Picture = ART[art];
  return (
    <svg key={art} className={`sja sja-${art}`} viewBox="0 0 220 160" aria-hidden="true">
      <Picture />
    </svg>
  );
}

// ---------------------------------------------------------------------------
// Tip pictures (shown only when a tip is opened)
// ---------------------------------------------------------------------------

export type TipArt =
  | 'review'
  | 'clear'
  | 'new'
  | 'learn'
  | 'keys'
  | 'separator'
  | 'pause'
  | 'aiq'
  | 'plain'
  | 'private'
  | 'speed'
  | 'gauge'
  | 'autopilot'
  | 'export'
  | 'lookup'
  | 'carry'
  | 'math'
  | 'assign'
  | 'folders'
  | 'windows'
  | 'barcode'
  | 'focus'
  | 'search'
  | 'undo';

const page = (x: number, y: number, key?: string | number) => (
  <g key={key}>
    <rect className="sja-face" x={x} y={y} width={30} height={40} rx={3} />
    <rect className="sja-ink" x={x + 5} y={y + 6} width={14} height={3} rx={1} />
    <rect className="sja-line" x={x + 5} y={y + 14} width={20} height={2.5} rx={1} />
    <rect className="sja-line" x={x + 5} y={y + 20} width={16} height={2.5} rx={1} />
  </g>
);

const tipArt: Record<TipArt, () => ReactElement> = {
  review: () => (
    <>
      {page(40, 40)}
      <path className="sja-flow" d="M80 60 H120" />
      <rect className="sja-tray" x={124} y={46} width={60} height={34} rx={6} />
      <g className="sja-wait">
        <rect className="sja-face" x={140} y={36} width={28} height={36} rx={3} />
        <circle className="sja-dot-amber" cx={160} cy={44} r={4} />
      </g>
      <path className="sja-flow" d="M190 60 H226" />
      <g className="sja-ok sja-pop">
        <circle cx={240} cy={60} r={10} />
        <path d="M235 60l3 3 6-6" />
      </g>
    </>
  ),
  clear: () => (
    <>
      {page(30, 38)}
      <g className="sja-ok sja-pop">
        <circle cx={64} cy={42} r={8} />
        <path d="M60 42l2.5 2.5 5-5" />
      </g>
      <path className="sja-flow" d="M80 58 H112" />
      {[0, 1, 2].map((index) => (
        <g key={index} className="sja-release" style={{ animationDelay: `${index * 0.35}s` }}>
          {page(124 + index * 40, 38)}
          <g className="sja-ok">
            <circle cx={150 + index * 40} cy={42} r={7} />
            <path d={`M${146.5 + index * 40} 42l2.5 2.5 4.5-4.5`} />
          </g>
        </g>
      ))}
    </>
  ),
  new: () => (
    <>
      {page(40, 36)}
      {page(84, 36)}
      <g className="sja-pop">
        <rect className="sja-face sja-new" x={150} y={30} width={36} height={48} rx={3} />
        <rect className="sja-orange-fill" x={156} y={36} width={24} height={6} rx={2} />
        <rect className="sja-line" x={156} y={50} width={24} height={3} rx={1} />
        <rect className="sja-line" x={156} y={57} width={18} height={3} rx={1} />
      </g>
      <text className="sja-q" x={198} y={52}>
        ?
      </text>
      <path className="sja-flow" d="M196 70 H230" />
      <g className="sja-ok sja-pop">
        <circle cx={244} cy={70} r={10} />
        <path d="M239 70l3 3 6-6" />
      </g>
    </>
  ),
  learn: () => (
    <>
      {page(36, 40)}
      <path className="sja-flow" d="M74 60 H108" />
      <g className="sja-brain">
        <path d="M130 48c-8 0-12 6-11 12-6 2-6 12 1 14 0 7 8 10 13 6 4 4 11 3 13-2 6 2 12-3 10-9 5-3 4-12-2-14 1-7-7-11-12-8-3-4-9-4-12 1z" />
      </g>
      <path className="sja-flow" d="M168 60 H200" />
      <g className="sja-grow">
        <rect className="sja-bar-base" x={208} y={74} width={10} height={10} rx={2} />
        <rect className="sja-bar-base" x={222} y={64} width={10} height={20} rx={2} />
        <rect className="sja-bar-up" x={236} y={50} width={10} height={34} rx={2} />
      </g>
    </>
  ),
  keys: () => (
    <>
      <g className="sja-key">
        <rect className="sja-face" x={46} y={44} width={70} height={36} rx={7} />
        <text className="sja-keytext" x={81} y={67}>
          Ctrl
        </text>
      </g>
      <text className="sja-plus" x={128} y={68}>
        +
      </text>
      <g className="sja-key sja-press">
        <rect className="sja-face" x={146} y={44} width={88} height={36} rx={7} />
        <text className="sja-keytext" x={190} y={67}>
          Enter
        </text>
      </g>
    </>
  ),
  separator: () => (
    <>
      {page(54, 40)}
      {page(58, 36)}
      <g className="sja-slide">
        <rect className="sja-sheet" x={112} y={30} width={34} height={46} rx={3} />
        <path className="sja-sheet-mark" d="M119 40h20M119 48h20M119 56h20M127 36v34M133 36v34" />
      </g>
      {page(164, 40)}
      {page(168, 36)}
      <line className="sja-cut sja-cut-on" x1={104} y1={26} x2={104} y2={94} />
      <line className="sja-cut sja-cut-on" x1={156} y1={26} x2={156} y2={94} />
    </>
  ),
  pause: () => (
    <>
      <circle className="sja-face" cx={140} cy={60} r={34} />
      <g className="sja-breathe">
        <rect className="sja-ink-strong" x={126} y={44} width={9} height={32} rx={2} />
        <rect className="sja-ink-strong" x={145} y={44} width={9} height={32} rx={2} />
      </g>
    </>
  ),
  aiq: () => (
    <>
      <g className="sja-brain sja-big">
        <path d="M140 30c-12 0-18 9-16 18-9 3-9 18 1 21 0 10 12 15 19 9 6 6 17 5 19-3 9 3 18-4 15-13 7-5 6-18-3-21 2-10-10-16-18-12-4-6-13-6-17 1z" />
      </g>
      <text className="sja-score" x={142} y={66}>
        AIQ
      </text>
      <path className="sja-spark" d="M196 34l3 7 7 3-7 3-3 7-3-7-7-3 7-3z" />
    </>
  ),
  plain: () => (
    <>
      <rect className="sja-face" x={92} y={14} width={96} height={92} rx={5} />
      <rect className="sja-ink" x={102} y={24} width={38} height={6} rx={2} />
      {[0, 1, 2].map((index) => (
        <rect
          key={index}
          className="sja-found"
          style={{ animationDelay: `${0.3 + index * 0.5}s` }}
          x={100}
          y={40 + index * 18}
          width={80}
          height={12}
          rx={3}
        />
      ))}
      {[44, 62, 80].map((y) => (
        <rect key={y} className="sja-line" x={104} y={y} width={64} height={4} rx={2} />
      ))}
    </>
  ),
  private: () => (
    <>
      <rect className="sja-face" x={92} y={22} width={96} height={64} rx={5} />
      <rect className="sja-line" x={128} y={86} width={24} height={10} />
      <rect className="sja-line" x={116} y={96} width={48} height={4} rx={2} />
      {page(104, 32)}
      <g className="sja-lock">
        <rect x={150} y={52} width={24} height={20} rx={4} />
        <path d="M155 52v-6a7 7 0 0 1 14 0v6" />
      </g>
    </>
  ),
  speed: () => (
    <>
      <rect className="sja-face" x={20} y={34} width={118} height={58} rx={6} />
      <rect className="sja-line" x={20} y={92} width={64} height={8} rx={2} />
      {[56, 104].map((cx) => (
        <g key={cx} className="sja-fan">
          <circle className="sja-fan-line" cx={cx} cy={63} r={18} />
          <path
            className="sja-fan-line"
            d={`M${cx} 45v36M${cx - 18} 63h36M${cx - 13} 50l26 26M${cx + 13} 50l-26 26`}
          />
        </g>
      ))}
      <path className="sja-bolt" d="M160 30l-14 24h11l-4 22 18-28h-12l5-18z" />
      {[
        [186, 48, 206],
        [180, 62, 210],
        [186, 76, 204],
      ].map(([x1, y, x2]) => (
        <line key={y} className="sja-streak" x1={x1} y1={y} x2={x2} y2={y} />
      ))}
      {[0, 0.55, 1.1].map((delay) => (
        <g key={delay} className="sja-zoom" style={{ animationDelay: `${delay}s` }}>
          {page(212, 40)}
        </g>
      ))}
    </>
  ),
  gauge: () => (
    <>
      <path className="sja-gauge" d="M90 90 A50 50 0 0 1 190 90" />
      <path className="sja-gauge-on" d="M90 90 A50 50 0 0 1 172 55" />
      <g className="sja-needle">
        <line x1={140} y1={90} x2={172} y2={58} />
        <circle cx={140} cy={90} r={5} />
      </g>
    </>
  ),
  autopilot: () => (
    <>
      {page(30, 40)}
      <path className="sja-flow" d="M66 60 H96" />
      <rect className="sja-face" x={100} y={36} width={56} height={48} rx={8} />
      <path className="sja-bolt" d="M131 44l-10 16h8l-3 14 13-19h-9l4-11z" />
      {[0, 0.6, 1.2].map((delay) => (
        <g key={delay} className="sja-out" style={{ animationDelay: `${delay}s` }}>
          {page(168, 40)}
          <g className="sja-ok">
            <circle cx={194} cy={44} r={6} />
            <path d="M191 44l2 2 4-4" />
          </g>
        </g>
      ))}
    </>
  ),
  export: () => (
    <>
      {page(30, 40)}
      <path className="sja-flow" d="M68 60 H104" />
      <g className="sja-fan-out">
        <path className="sja-dest" d="M120 30h20l6 6h34v26h-60z" />
        <path
          className="sja-dest"
          d="M196 46a14 14 0 0 1 27-4 11 11 0 1 1 2 22h-29a9 9 0 0 1 0-18z"
        />
        <rect className="sja-dest" x={124} y={74} width={56} height={30} rx={5} />
      </g>
      <g className="sja-send">{page(104, 42)}</g>
    </>
  ),
  lookup: () => (
    <>
      <rect className="sja-face" x={30} y={24} width={100} height={72} rx={5} />
      {[0, 1, 2, 3].map((row) => (
        <rect
          key={row}
          className={row === 2 ? 'sja-row-on' : 'sja-line'}
          x={38}
          y={34 + row * 16}
          width={84}
          height={8}
          rx={2}
        />
      ))}
      <path className="sja-flow" d="M136 66 H170" />
      <rect className="sja-face" x={176} y={52} width={80} height={28} rx={5} />
      <rect className="sja-found sja-fill" x={182} y={60} width={60} height={12} rx={3} />
    </>
  ),
  carry: () => (
    <>
      <rect className="sja-face" x={24} y={28} width={80} height={56} rx={5} />
      <rect className="sja-face" x={176} y={28} width={80} height={56} rx={5} />
      <g className="sja-carry">
        <rect className="sja-box" x={120} y={46} width={40} height={30} rx={4} />
        <path className="sja-tick" d="M131 61l5 5 10-11" />
      </g>
    </>
  ),
  math: () => (
    <>
      <rect className="sja-face" x={18} y={40} width={244} height={40} rx={6} />
      <text className="sja-num" x={44} y={66}>
        3
      </text>
      <text className="sja-op" x={66} y={66}>
        ×
      </text>
      <text className="sja-num" x={112} y={66}>
        24.50
      </text>
      <text className="sja-op" x={150} y={66}>
        =
      </text>
      <text className="sja-num sja-num-strong" x={196} y={66}>
        73.50
      </text>
      <g className="sja-ok sja-pop">
        <circle cx={242} cy={60} r={10} />
        <path d="M237 60l3 3 6-6" />
      </g>
    </>
  ),
  assign: () => (
    <>
      {page(30, 30)}
      <rect className="sja-found" x={33} y={42} width={26} height={7} rx={2} />
      <g className="sja-pop">
        <rect className="sja-face" x={78} y={34} width={92} height={46} rx={6} />
        <rect className="sja-line" x={88} y={44} width={40} height={5} rx={2} />
        <rect className="sja-row-on" x={84} y={56} width={80} height={14} rx={3} />
        <rect className="sja-ink" x={90} y={60} width={56} height={5} rx={2} />
      </g>
      <path className="sja-flow" d="M176 57 H204" />
      <rect className="sja-face" x={210} y={44} width={54} height={26} rx={5} />
      <rect className="sja-found sja-fill" x={216} y={51} width={42} height={12} rx={3} />
    </>
  ),
  folders: () => (
    <>
      {[0, 1, 2].map((level) => (
        <g key={level} className="sja-step" style={{ animationDelay: `${level * 0.5}s` }}>
          <path
            className="sja-folder"
            d={`M${40 + level * 34} ${28 + level * 26}h16l5 5h23v17h-44z`}
          />
          <rect
            className="sja-line"
            x={92 + level * 34}
            y={36 + level * 26}
            width={60}
            height={5}
            rx={2}
          />
        </g>
      ))}
      <g className="sja-step" style={{ animationDelay: '1.5s' }}>
        {page(146, 76)}
      </g>
    </>
  ),
  windows: () => (
    <>
      <rect className="sja-face" x={22} y={22} width={110} height={70} rx={5} />
      <rect className="sja-line" x={64} y={92} width={26} height={10} />
      <rect className="sja-line" x={52} y={102} width={50} height={4} rx={2} />
      <rect className="sja-face" x={148} y={22} width={110} height={70} rx={5} />
      <rect className="sja-line" x={190} y={92} width={26} height={10} />
      <rect className="sja-line" x={178} y={102} width={50} height={4} rx={2} />
      {page(62, 32)}
      <g className="sja-move">
        {[0, 1, 2].map((row) => (
          <rect
            key={row}
            className={row === 0 ? 'sja-row-on' : 'sja-line'}
            x={90}
            y={36 + row * 14}
            width={34}
            height={8}
            rx={2}
          />
        ))}
      </g>
    </>
  ),
  barcode: () => (
    <>
      <g className="sja-ink-strong">
        {[0, 4, 7, 12, 15, 19, 24, 27, 31, 35, 38, 43, 46, 50].map((x, index) => (
          <rect key={x} x={40 + x} y={34} width={index % 3 === 0 ? 3 : 1.6} height={44} />
        ))}
      </g>
      <rect className="sja-laser" x={38} y={30} width={2.5} height={52} />
      <path className="sja-flow" d="M104 56 H150" />
      <rect className="sja-face" x={156} y={42} width={100} height={28} rx={5} />
      <rect className="sja-found sja-fill" x={162} y={50} width={76} height={12} rx={3} />
    </>
  ),
  focus: () => (
    <>
      {[0, 1, 2].map((row) => (
        <g key={row} className="sja-step" style={{ animationDelay: `${row * 0.45}s` }}>
          <rect className="sja-face" x={40} y={22 + row * 30} width={200} height={24} rx={5} />
          <rect className="sja-line" x={50} y={31 + row * 30} width={54} height={6} rx={2} />
          <rect className="sja-crop" x={150} y={26 + row * 30} width={82} height={16} rx={3} />
          <rect className="sja-ink" x={158} y={31 + row * 30} width={44} height={5} rx={2} />
        </g>
      ))}
    </>
  ),
  search: () => (
    <>
      {page(50, 40)}
      {page(96, 36)}
      {page(142, 40)}
      <rect className="sja-found" x={146} y={52} width={22} height={6} rx={2} />
      <g className="sja-scan">
        <circle className="sja-lens" cx={190} cy={52} r={16} />
        <path className="sja-lens" d="M202 64l14 14" />
      </g>
    </>
  ),
  undo: () => (
    <>
      {page(104, 40)}
      <g className="sja-ok sja-pop">
        <circle cx={140} cy={44} r={9} />
        <path d="M135.5 44l3 3 6-6" />
      </g>
      <path className="sja-undo" d="M190 76a34 34 0 1 0-12 26" />
      <path className="sja-undo" d="M190 64v12h-12" />
    </>
  ),
};

export function SetupTipArt({ art }: { art: TipArt }) {
  const Picture = tipArt[art];
  return (
    <svg className={`sja sja-tip sja-tip-${art}`} viewBox="0 0 280 120" aria-hidden="true">
      <Picture />
    </svg>
  );
}
